import { addDays } from 'admin-panel/utils/hotel-time';

import type { DashboardChartMode } from 'admin-panel/types/dashboard';
import type { DashboardBucket } from 'admin-panel/utils/dashboard-buckets';
import type { DashboardTimeframe } from 'admin-panel/utils/dashboard-timeframe';

export interface DashboardChartBarSegment {
  id: string;
  value: number;
}

export interface DashboardChartBar {
  label: string;
  tooltipTitle: string;
  value: number;
  segments?: DashboardChartBarSegment[];
}

export type DashboardChartLegend = 'hours' | 'days' | 'weeks';

const A_MONDAY = Date.UTC(2026, 0, 5);
const DAY_IN_MS = 24 * 60 * 60 * 1000;

export function dashboardChartLegend(
  mode: DashboardChartMode,
  timeframe: DashboardTimeframe
): DashboardChartLegend {
  if (mode === 'weekday-average') {
    return 'days';
  }

  if (mode === 'hourly-average' || timeframe.totalsInterval === 'hourly') {
    return 'hours';
  }

  return timeframe.totalsInterval === 'weekly' ? 'weeks' : 'days';
}

export function dashboardChartBars(
  buckets: DashboardBucket[],
  mode: DashboardChartMode,
  timeframe: DashboardTimeframe,
  locale: string
): DashboardChartBar[] {
  const legend = dashboardChartLegend(mode, timeframe);

  return buckets.map((bucket, index) => {
    if (mode === 'weekday-average') {
      const weekday = new Date(A_MONDAY + Number(bucket.key) * DAY_IN_MS);

      return {
        label: format(weekday, locale, { weekday: 'short' }),
        tooltipTitle: format(weekday, locale, { weekday: 'long' }),
        value: bucket.value,
      };
    }

    if (legend === 'hours') {
      const hour = Number(bucket.key);

      return {
        label: formatHour(hour, locale),
        tooltipTitle: `${formatHour(hour, locale)} – ${formatHour(
          (hour + 1) % 24,
          locale
        )}`,
        value: bucket.value,
      };
    }

    const start = dateOf(bucket.key);

    if (legend === 'weeks') {
      const nextStart = buckets[index + 1]?.key ?? timeframe.endDateExclusive;

      return {
        label: format(start, locale, { day: 'numeric', month: 'short' }),
        tooltipTitle: `${formatDate(start, locale)} – ${formatDate(
          dateOf(addDays(nextStart, -1)),
          locale
        )}`,
        value: bucket.value,
      };
    }

    return {
      label: format(start, locale, { day: 'numeric', month: 'short' }),
      tooltipTitle: format(start, locale, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      }),
      value: bucket.value,
    };
  });
}

export function dashboardStackedChartBars(
  series: { id: string; buckets: DashboardBucket[] }[],
  mode: DashboardChartMode,
  timeframe: DashboardTimeframe,
  locale: string
): DashboardChartBar[] {
  const totals = (series[0]?.buckets ?? []).map((bucket, index) => ({
    key: bucket.key,
    value: series.reduce(
      (sum, { buckets }) => sum + (buckets[index]?.value ?? 0),
      0
    ),
  }));

  return dashboardChartBars(totals, mode, timeframe, locale).map(
    (bar, index) => ({
      ...bar,
      segments: series.map(({ id, buckets }) => ({
        id,
        value: buckets[index]?.value ?? 0,
      })),
    })
  );
}

function formatHour(hour: number, locale: string) {
  return format(new Date(Date.UTC(2026, 0, 1, hour)), locale, {
    hour: 'numeric',
  });
}

function formatDate(date: Date, locale: string) {
  return format(date, locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function format(
  date: Date,
  locale: string,
  options: Intl.DateTimeFormatOptions
) {
  return new Intl.DateTimeFormat(locale, {
    ...options,
    timeZone: 'UTC',
  }).format(date);
}

function dateOf(key: string) {
  return new Date(`${key}T00:00:00Z`);
}
